import { useTranslate } from '@refinedev/core';
import { Alert, App as AntdApp, Input, Space, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import {
  SectionDrawer,
  ToolDatePicker,
  dateOfDay,
  dayOf,
} from '../../components/ToolSection';
import {
  defaultStart,
  planInputOf,
  planProblem,
  prefillOf,
  softNote,
  type SummaryRow,
} from './model';
import { useFaCommand } from './useFaCommand';

/**
 * E48 · PlanContingencyChange — the Plan a change drawer, opened from the behavior's own card. The read-only context is what the
 * therapist needs while writing: the soft note when the records are few (a warning, never a block), the client's ideas as chips that fill
 * "What changes", and the top of what comes before and after the behavior (the change targets one of them).
 */
type Tx = (key: string, options?: Record<string, unknown>) => string;

const TOP = 3;

type Props = {
  open: boolean;
  clientId: string;
  targetId: string;
  summary: SummaryRow;
  onClose: () => void;
  onSaved: () => void;
};

export const FaPlanDrawer = ({
  open,
  clientId,
  targetId,
  summary,
  onClose,
  onSaved,
}: Props) => {
  const t = useTranslate() as unknown as Tx;
  const { message } = AntdApp.useApp();
  const { run, isLoading } = useFaCommand();
  const prefill = prefillOf(summary.suggestion);
  const initialWhat = prefill?.text ?? '';
  const [what, setWhat] = useState(initialWhat);
  const [touched, setTouched] = useState(false);
  const [expected, setExpected] = useState('');
  const [startsOn, setStartsOn] = useState<Date>(() =>
    dateOfDay(defaultStart(new Date())),
  );
  const [opening, setOpening] = useState(() => defaultStart(new Date()));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const start = defaultStart(new Date());
    setWhat(initialWhat);
    setTouched(false);
    setExpected('');
    setStartsOn(dateOfDay(start));
    setOpening(start);
    setError(null);
    // a fresh form each time it opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const note = softNote(summary);
  const ideas = summary.recurring?.nextTime ?? [];
  const toolName = (tool?: string | null) =>
    tool ? t(`fa.tool.${tool}`) : null;
  const around = [
    ['vulnerabilities', 'fa.hub.vulnerabilities', summary.recurring?.vulnerabilities],
    ['promptingEvents', 'fa.hub.promptingEvents', summary.recurring?.promptingEvents],
    ['rightAway', 'fa.hub.rightAway', summary.recurring?.rightAway],
    ['later', 'fa.hub.later', summary.recurring?.later],
  ] as const;

  const onSave = async () => {
    const d = {
      whatChanges: what,
      expectedEffect: expected,
      startsOn: dayOf(startsOn),
    };
    if (planProblem(d)) {
      setError(t('fa.change.required'));
      return;
    }
    setError(null);
    const res = await run(
      'planContingencyChange',
      planInputOf(clientId, targetId, d),
      t('fa.errors.FA_VALUE_INVALID'),
    );
    if (res.ok) {
      message.success(t('fa.change.saved'));
      onSaved();
    } else {
      setError(res.message);
    }
  };

  return (
    <SectionDrawer
      open={open}
      title={t('fa.change.title')}
      testId="fa-plan"
      saveTestId="fa-plan-submit"
      width="wide"
      context={
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Typography.Text type="secondary">{t('fa.change.intro')}</Typography.Text>
          {note.show ? (
            <Alert
              type="warning"
              showIcon
              message={t('fa.change.fewRecords', { n: note.n, max: note.max })}
              data-testid="fa-plan-soft-note"
            />
          ) : null}
          {ideas.length ? (
            <div data-testid="fa-plan-ideas">
              <Typography.Text strong>{t('fa.change.ideas')}</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Space wrap size={[8, 8]}>
                  {ideas.map((x, i) => (
                    <Tag
                      key={x.instead}
                      data-testid={`fa-plan-idea-${i}`}
                      style={{ cursor: 'pointer', whiteSpace: 'normal' }}
                      onClick={() => {
                        setWhat(x.instead);
                        setTouched(true);
                        setError(null);
                      }}
                    >
                      {x.instead} · {x.times}
                      {x.tool ? ` · ${toolName(x.tool)}` : ''}
                    </Tag>
                  ))}
                </Space>
              </div>
            </div>
          ) : null}
          <div data-testid="fa-plan-around">
            <Typography.Text strong>{t('fa.change.context')}</Typography.Text>
            {around.map(([id, key, rows]) =>
              rows && rows.length ? (
                <div key={id} data-testid={`fa-plan-around-${id}`}>
                  <Typography.Text type="secondary">{t(key)}: </Typography.Text>
                  {rows
                    .slice(0, TOP)
                    .map((r) => `${r.text} (${r.count})`)
                    .join(' · ')}
                </div>
              ) : null,
            )}
          </div>
        </Space>
      }
      saveLabel={t('fa.change.plan')}
      saving={isLoading}
      error={error}
      dirty={
        what !== initialWhat || expected !== '' || dayOf(startsOn) !== opening
      }
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Typography.Text>{t('fa.change.whatChanges')}</Typography.Text>
          {prefill && !touched ? (
            <Typography.Text
              type="secondary"
              style={{ display: 'block' }}
              data-testid="fa-plan-prefill"
            >
              {t('fa.change.prefill', { n: prefill.times })}
              {prefill.tool ? (
                <Tag style={{ marginLeft: 8 }}>{toolName(prefill.tool)}</Tag>
              ) : null}
            </Typography.Text>
          ) : null}
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 5 }}
            data-testid="fa-plan-what"
            maxLength={1000}
            onChange={(e) => {
              setWhat(e.target.value);
              setTouched(true);
              setError(null);
            }}
            value={what}
          />
        </div>
        <div>
          <Typography.Text>{t('fa.change.expected')}</Typography.Text>
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            data-testid="fa-plan-expected"
            maxLength={1000}
            onChange={(e) => {
              setExpected(e.target.value);
              setError(null);
            }}
            value={expected}
          />
        </div>
        <div style={{ maxWidth: 260 }}>
          <Typography.Text>{t('fa.change.from')}</Typography.Text>
          <ToolDatePicker
            testId="fa-plan-from"
            value={startsOn}
            onChange={(d) => {
              if (d) setStartsOn(d);
              setError(null);
            }}
          />
        </div>
      </Space>
    </SectionDrawer>
  );
};
